import { HttpClient } from '@angular/common/http';
import { Component, Inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { DefaultValuesService } from '../../../../../../services/defaultvalues.service';
import { CustomDialogService } from '../../../../../../services/customdialog.service';
import { AddFactorPopupComponent } from '../add-factor-popup/add-factor-popup.component';
import { ProtocolFactorChangelogComponent } from '../protocol-factor-changelog/protocol-factor-changelog.component';
import { SelectionModel } from '@angular/cdk/collections';
import { TableColumn } from '../../../../shared-components/data-table/data-table.component';

@Component({
    selector: 'app-protocol-factor-table',
    templateUrl: './protocol-factor-table.component.html',
    styleUrls: ['./protocol-factor-table.component.less',
        '../../oshadmin.component.less',
        '../../../../../../style/new-generic-styles.component.less',
        '../../../../../../style/mat-table.component.less'],
    standalone: false
})
export class ProtocolFactorTableComponent implements OnChanges, OnDestroy {

    @Input() protocol: any;

    protocolFactors: any[] = [];
    tableColumns: TableColumn[] = [];
    readonly emptyFactorsMessage = 'No factors found for this protocol. Click "Add Factor" to create the first factor.';

    isRemoveActive: boolean = false;
    removeList: number[] = [];
    selection = new SelectionModel<any>(true, []);
    
    private readonly _onDestroy = new Subject<void>();

    constructor(
        private readonly http: HttpClient,
        private readonly dialogForm: MatDialog,
        @Inject('BASE_URL') private readonly baseUrl: string,
        public defaults: DefaultValuesService,
        private readonly toastrService: ToastrService,
        private readonly customDialog: CustomDialogService,
        private readonly router: Router
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['protocol'] && this.protocol) {
            this.protocolFactors = this.protocol?.protocolFactors ?? [];
            this.buildColumns();
        }
    }

    private buildColumns(): void {
        const columns: TableColumn[] = [
            { key: 'code', header: 'Code', width: '7%', align: 'left', editable: false },
            { key: 'description', header: 'Description', width: '15%', align: 'left', editable: false },
            { key: 'stage1', header: 'Foundation', width: '15%', align: 'left', editable: false },
            { key: 'stage2', header: 'Demostrating', width: '15%', align: 'left', editable: false },
            { key: 'stage3', header: 'Performing', width: '15%', align: 'left', editable: false },
            { key: 'stage4', header: 'Excelling', width: '15%', align: 'left', editable: false }
        ];

        if (this.defaults.isMersyAdminUser || this.defaults.isMersyAdminSafetySystemExcellence) {
            columns.push(
                { key: 'actions', header: 'Actions', width: '8%', align: 'center', editable: false },
                { key: 'changeLog', header: 'Change Log', width: '10%', align: 'center', editable: false }
            );
        }

        this.tableColumns = columns;
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    loadProtocolFactors() {
        this.http.get(this.baseUrl + `api/osh/protocols/${this.protocol.id}/factors`)
            .subscribe({
                next: (result: any) => {
                    this.protocolFactors = result || [];
                },
                error: (error) => {
                    console.error('Error loading protocol factors:', error);
                    this.toastrService.error('Error loading protocol factors');
                }
            });
    }

    addFactor() {
        const dialogRef = this.dialogForm.open(AddFactorPopupComponent, {
            panelClass: ['custom-mat-dialog_v2', 'dialogNgselect'],
            disableClose: true,
            width: '600px',
            maxHeight: '90vh',
            data: {
                protocolId: this.protocol.id,
                protocolName: this.protocol.name,
                protocolCode: this.protocol.code,
                isEditMode: false
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadProtocolFactors();
            }
        });
    }

    editFactor(factor: any) {
        const dialogRef = this.dialogForm.open(AddFactorPopupComponent, {
            panelClass: ['custom-mat-dialog_v2', 'dialogNgselect'],
            disableClose: true,
            width: '600px',
            maxHeight: '90vh',
            data: {
                protocolId: this.protocol.id,
                protocolName: this.protocol.name,
                protocolCode: this.protocol.code,
                factor: factor,
                isEditMode: true
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadProtocolFactors();
            }
        });
    }

    deleteFactor(factor: any) {
        this.customDialog.openConfirm({
            title: "Delete Protocol Factor",
            message: `Do you want to delete the factor "${factor.code}"?`,
            caller: this
        });

        this.customDialog.confirmed().subscribe(res => {
            if (res.confirmed) {
                this.http.delete(this.baseUrl + `api/osh/protocol-factors/${factor.id}`)
                    .subscribe({
                        next: () => {
                            this.toastrService.success('Protocol factor deleted successfully');
                            this.loadProtocolFactors();
                        },
                        error: (error) => {
                            console.error('Error deleting protocol factor:', error);
                            this.toastrService.error('Error deleting protocol factor');
                        }
                    });
            }
        });
    }

    toggleRemoveFactor() {
        this.isRemoveActive = !this.isRemoveActive;
        if (!this.isRemoveActive) {
            this.selection.clear();
            this.removeList = [];
        }
    }

    textRemButtonState() {
        return this.selection.selected.length > 0 ? 'Remove selected items' : 'Remove All';
    }

    selectDeselectItem(isChecked: boolean, factorId: number) {
        if (this.isRemoveActive) {
            if (isChecked) {
                this.selection.select(factorId);
                this.onSelect(factorId);
            } else {
                this.selection.deselect(factorId);
                this.onDeselect(factorId);
            }
        }
    }

    onSelect(selectedRemoveRow: number) {
        if (!this.removeList.includes(selectedRemoveRow)) {
            this.removeList.push(selectedRemoveRow);
        }
    }

    onDeselect(selectedRemoveRow: number) {
        const index = this.removeList.indexOf(selectedRemoveRow);
        if (index !== -1) {
            this.removeList.splice(index, 1);
        }
    }

    onClickBulkRemove() {
        const factorIdsToRemove = this.selection.selected.length > 0 ? this.removeList : this.protocolFactors.map(factor => factor.id);
        const message = this.selection.selected.length > 0 
            ? `Do you want to remove ${this.selection.selected.length} selected factors?`
            : `Do you want to remove all ${this.protocolFactors.length} factors?`;

        this.customDialog.openConfirm({
            title: "Remove Protocol Factors",
            message: `${message} Please note that this action cannot be rolled back!`,
            caller: this
        });

        this.customDialog.confirmed().subscribe(res => {
            if (res.confirmed) {
                this.http.delete(this.baseUrl + `api/osh/protocol-factors/bulk`, {
                    body: factorIdsToRemove
                }).subscribe({
                    next: () => {
                        this.toastrService.success('Protocol factors were removed successfully!');
                        this.toggleRemoveFactor();
                        this.loadProtocolFactors();
                    },
                    error: (error) => {
                        console.error('Error removing protocol factors:', error);
                        this.toastrService.error('Error removing protocol factors');
                    }
                });
            }
        });
    }

    viewFactorHistory(factor: any) {
        this.dialogForm.open(ProtocolFactorChangelogComponent, {
            disableClose: false,
            width: '1100px',
            height: '65vh',
            data: {
                element: {
                    code: factor.code,
                    description: factor.description,
                    protocolName: this.protocol.name,
                    protocolCode: this.protocol.code
                },
                dataType: 'ProtocolFactor',
                factorId: factor.id
            }
        });
    }
}